import { useEffect, useRef } from 'react'
import { getUserSettings, putUserSettings } from './api'
import { useSession } from './useSession'
import { useUiStore } from './uiStore'

/** Load server settings after auth; debounce-save on change. */
export function useSettingsSync() {
  const { isAuthenticated } = useSession()
  const applyServerSettings = useUiStore((s) => s.applyServerSettings)
  const setSettingsHydrated = useUiStore((s) => s.setSettingsHydrated)
  const settingsHydrated = useUiStore((s) => s.settingsHydrated)
  const toServerPatch = useUiStore((s) => s.toServerPatch)

  const locale = useUiStore((s) => s.locale)
  const catalogCode = useUiStore((s) => s.catalogCode)
  const theme = useUiStore((s) => s.theme)
  const background = useUiStore((s) => s.background)
  const fontSizePx = useUiStore((s) => s.fontSizePx)
  const listDensity = useUiStore((s) => s.listDensity)
  const aiPrivacy = useUiStore((s) => s.aiPrivacy)
  const a11yHighContrast = useUiStore((s) => s.a11yHighContrast)
  const a11yReduceMotion = useUiStore((s) => s.a11yReduceMotion)
  const a11yComfort = useUiStore((s) => s.a11yComfort)
  const a11yVoiceConfirm = useUiStore((s) => s.a11yVoiceConfirm)
  const accountPrefs = useUiStore((s) => s.accountPrefs)

  const skipSave = useRef(true)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastPrefsJson = useRef<string>('')

  useEffect(() => {
    if (!isAuthenticated) {
      setSettingsHydrated(false)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const res = await getUserSettings()
        if (cancelled) return
        skipSave.current = true
        applyServerSettings(res.settings)
        lastPrefsJson.current = JSON.stringify(res.settings.account_prefs || {})
      } catch (e) {
        console.warn('settings hydrate failed', e)
        if (!cancelled) setSettingsHydrated(true)
      } finally {
        setTimeout(() => {
          skipSave.current = false
        }, 80)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, applyServerSettings, setSettingsHydrated])

  // Immediate persist for account_prefs (DoD: color survives refresh)
  useEffect(() => {
    if (!isAuthenticated || !settingsHydrated || skipSave.current) return
    const json = JSON.stringify(accountPrefs)
    if (json === lastPrefsJson.current) return
    lastPrefsJson.current = json
    void putUserSettings({ account_prefs: accountPrefs })
      .then(() => {
        /* ok */
      })
      .catch((e) => console.warn('account_prefs save failed', e))
  }, [isAuthenticated, settingsHydrated, accountPrefs])

  useEffect(() => {
    if (!isAuthenticated || !settingsHydrated || skipSave.current) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      void putUserSettings(toServerPatch()).catch((e) => {
        console.warn('settings save failed', e)
      })
    }, 400)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [
    isAuthenticated,
    settingsHydrated,
    locale,
    catalogCode,
    theme,
    background,
    fontSizePx,
    listDensity,
    aiPrivacy,
    a11yHighContrast,
    a11yReduceMotion,
    a11yComfort,
    a11yVoiceConfirm,
    accountPrefs,
    toServerPatch,
  ])
}
